/**
 * SettingsShell — Sub-Layout für den `/settings`-Subtree.
 *
 * Ursprünglich (Unified Settings-IA, 2026-06-09) konsolidierte dieser Shell drei
 * Tabs: Profil, Team, Vokabular. Mit dem P1c-Login-Cutover (Must 12, D-P1c-9)
 * sind **Profil und Team nach tools.greenforce.com gewandert** — dort sitzt ab
 * jetzt die Identität (ein Profil für alle Apps, eine Team-Verwaltung). Was in
 * DAM bleibt, ist das **Vokabular**: eine DAM-Domänen-Einstellung, keine
 * Identität.
 *
 * Damit bleibt genau ein Tab übrig. Die Tab-Leiste rendert trotzdem weiter —
 * (a) als Orientierungs-Anker (die Route heißt weiter „Einstellungen"),
 * (b) weil ein künftiger DAM-eigener Settings-Bereich hier andockt, ohne die
 * IA neu bauen zu müssen.
 *
 * Verhalten für die abgegebenen Pfade:
 *   - `/settings/profile` und `/settings/team` → `window.location.replace()` auf
 *     den entsprechenden tools-Tab. Cross-Origin, deshalb KEIN
 *     wouter-`<Redirect>` (das kennt nur den App-eigenen History-Stack).
 *   - Member (kein Admin) haben in DAM gar keinen Tab mehr → JEDER
 *     `/settings`-Aufruf landet in den tools-Settings (Profil).
 *   - Admin, bare `/settings` oder unbekannter Subpfad → `/settings/vocab`.
 *
 * A11y: die Sub-Nav ist Route-Navigation (kein In-Page-Panel-Switch) →
 * `<nav aria-label="Einstellungen">` + `<Link aria-current="page">`, NICHT
 * ARIA `role="tablist"/"tab"`.
 *
 * Lazy/Suspense: die Page-Lazy-Def steht auf MODUL-Ebene (stabile Identität) —
 * innerhalb der Component aufgerufen erzeugte `React.lazy()` pro Render eine
 * neue Instanz → das innere `<Suspense>` würde flackern.
 */
import { Suspense, useEffect, type ReactNode } from 'react';
import { Link, Redirect, useLocation } from 'wouter';
import type { AuthedUser } from '../lib/auth-types';
import { lazyPageFactories } from '../lib/lazyPages';
import { lazyWithRetry } from '../lib/lazyWithRetry';
import { redirectToToolsSettings, type ToolsSettingsTab } from '../lib/tools-login';
import { Spinner } from './ui/Spinner';

// Modul-Ebene (Pflicht — siehe JSDoc): stabile Lazy-Identität, gemeinsame
// Import-Factory mit preloadRoutes (kein dual-string-source).
const LazyVocabAdminPage = lazyWithRetry(lazyPageFactories.VocabAdminPage);

type TabId = 'vocab';

type SettingsTab = {
  id: TabId;
  label: string;
  /** Absoluter Ziel-Pfad (kein nested Base). */
  path: string;
  /** Nur für Admins sichtbar/erreichbar. */
  adminOnly: boolean;
  render: (authedUser: AuthedUser) => ReactNode;
};

const TABS: readonly SettingsTab[] = [
  {
    id: 'vocab',
    label: 'Vokabular',
    path: '/settings/vocab',
    adminOnly: true,
    render: (u) => <LazyVocabAdminPage authedUser={u} />,
  },
] as const;

/**
 * Pfad-Segmente, die DAM an tools abgegeben hat (Must 12).
 *
 * Bewusst eine `Map` und kein Objekt-Literal: ein Lookup wie
 * `MOVED_TO_TOOLS[seg]` würde für `/settings/__proto__` oder
 * `/settings/toString` ein geerbtes `Object.prototype`-Member liefern — truthy,
 * und damit als „Tab" durchgereicht statt in den Unknown-Path-Redirect zu
 * fallen (Object-Injection, Codex-R3; bekannte Falle aus `patterns/`).
 */
const MOVED_TO_TOOLS = new Map<string, ToolsSettingsTab>([
  ['profile', 'profile'],
  ['team', 'team'],
]);

/** Letztes Pfadsegment unter `/settings` (z.B. '' | 'vocab' | 'profile' | 'foo'). */
function tabSegmentFrom(location: string): string {
  return location.replace(/^\/settings\/?/, '').split('/')[0] ?? '';
}

/**
 * Rendert einen Lade-Indikator und verlässt die App Richtung tools-Settings.
 * Navigation im Effect (nicht im Render): ein `location.replace()` im
 * Render-Pfad wäre ein Seiteneffekt am falschen Ort (unter StrictMode doppelt).
 */
function ToolsSettingsRedirect({ tab }: { tab: ToolsSettingsTab }) {
  useEffect(() => {
    redirectToToolsSettings(tab);
  }, [tab]);

  return (
    <div className="min-h-[200px] flex items-center justify-center">
      <Spinner size="lg" className="text-brand-green-700" />
      <span className="sr-only">Weiterleitung zu den Einstellungen …</span>
    </div>
  );
}

export function SettingsShell({ authedUser }: { authedUser: AuthedUser }) {
  const [location] = useLocation();
  const availableTabs = TABS.filter((t) => !t.adminOnly || authedUser.isAdmin);

  const seg = tabSegmentFrom(location);

  // Abgegebene Pfade zuerst — VOR jeder Rollen-Logik: `/settings/profile` gehört
  // JEDEM User auf tools, auch Admins (die hier sonst auf /settings/vocab
  // umgebogen würden und nie beim Profil ankämen).
  const movedTab = MOVED_TO_TOOLS.get(seg);
  if (movedTab) return <ToolsSettingsRedirect tab={movedTab} />;

  // Member haben in DAM keinen einzigen Settings-Tab mehr → alles, was hier noch
  // ankommt (bare `/settings`, `/settings/vocab`, Tippfehler), gehört auf tools.
  if (availableTabs.length === 0) return <ToolsSettingsRedirect tab="profile" />;

  const activeTab = availableTabs.find((t) => t.id === seg) ?? null;
  // Admin auf bare `/settings` oder unbekanntem Subpfad → erster verfügbarer Tab.
  if (!activeTab) return <Redirect to="/settings/vocab" replace />;

  return (
    <div className="flex flex-1 flex-col">
      <nav
        aria-label="Einstellungen"
        className="flex items-end gap-1 border-b border-ink-100 px-4 md:px-10 pt-6 md:pt-8"
      >
        {availableTabs.map((tab) => {
          const active = tab.id === activeTab.id;
          return (
            <Link
              key={tab.id}
              href={tab.path}
              aria-current={active ? 'page' : undefined}
              className={`-mb-px border-b-2 px-3 py-2 text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-2 rounded-t-md ${
                active
                  ? 'border-brand-green-700 text-brand-green-700 font-medium'
                  : 'border-transparent text-brand-green-600 hover:text-brand-green-800 hover:border-ink-200'
              }`}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      <Suspense
        fallback={
          <div className="min-h-[200px] flex items-center justify-center">
            <Spinner size="lg" className="text-brand-green-700" />
            <span className="sr-only">Lädt…</span>
          </div>
        }
      >
        {activeTab.render(authedUser)}
      </Suspense>
    </div>
  );
}
